/**
 * components/wishlist/attribution.tsx
 *
 * Renders the "Added by you" / "Added by [nickname]" line.
 * Two variants: compact (item row) and full (detail sheet).
 */

interface AttributionProps {
  isAddedByMe: boolean
  addedByNickname: string
  addedByAvatar: string
  /** ISO timestamp string or unix ms — shown in full variant */
  createdAt?: number
  variant?: 'compact' | 'full'
}

const timeAgo = (ts: number): string => {
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function Attribution({
  isAddedByMe,
  addedByNickname,
  addedByAvatar,
  createdAt,
  variant = 'compact',
}: AttributionProps) {
  if (variant === 'full') {
    return (
      <div className="flex items-center gap-2 py-3 border-b border-[#F9E0E8]">
        <div className="w-[28px] h-[28px] rounded-full bg-[#FBEAF0] border border-[#F4C0D1] flex items-center justify-center text-[14px]">
          {addedByAvatar}
        </div>
        <div>
          <span className="font-['DM_Sans'] text-[13px] text-[#2C1810]">
            {isAddedByMe ? 'Added by you' : `Added by ${addedByNickname}`}
          </span>
          {createdAt && (
            <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2] ml-2">
              · {timeAgo(createdAt)}
            </span>
          )}
        </div>
      </div>
    )
  }

  // compact variant (item row)
  return (
    <div className="flex items-center gap-[5px] mt-[3px]">
      {isAddedByMe ? (
        <span className="font-['DM_Sans'] text-[11px] italic text-[#B89AA2]">
          Added by you
        </span>
      ) : (
        <>
          <div className="w-[16px] h-[16px] rounded-full bg-[#FBEAF0] border border-[#F4C0D1] flex items-center justify-center text-[9px] flex-shrink-0">
            {addedByAvatar}
          </div>
          <span className="font-['DM_Sans'] text-[11px] italic text-[#B89AA2]">
            Added by {addedByNickname}
          </span>
        </>
      )}
    </div>
  )
}
